import React, { memo } from 'react'
import { Box } from '@mui/material'
import { useSubtleShimmerColor } from '../../components/common/SubtleShimmer'
import TechnicalsTimeframeToggle from '../../components/securityInfo/TechnicalsTimeframeToggle'

/**
 * Heights below are the exact line boxes of the real components so the layout does not
 * shift when the shimmer is swapped for TechnicalsContent.
 */
const LOGO_SIZE = 20
const HEADER_NAME_HEIGHT = 20.8 // body1 16px x lineHeight 1.3
const BACK_BUTTON_HEIGHT = 30.75 // small outlined Button: 13px x 1.75 + 3px padding + 1px border
const BACK_BUTTON_WIDTH = 102

const TABLE_TITLE_HEIGHT = 21 // 14px x lineHeight 1.5
const TH_HEIGHT = 31 // 12px x 1.5 + spacing(0.75) padding + 1px border
const TD_HEIGHT = 34.85 // 13.5px x 1.5 + spacing(0.85) padding + 1px border
const CELL_PADDING_X = 1 // spacing(1), matching th/td padding

const OSCILLATOR_ROWS = 11
const MOVING_AVERAGE_ROWS = 15
const PIVOT_ROWS = 7
const PIVOT_COLUMN_WIDTHS = [88, 88, 88, 88, 88]

/** Mirrors TechnicalsConfigBuilder.CLIENT_TIMEFRAMES so the chips size identically. */
const TIMEFRAME_PLACEHOLDERS = [
    { id: '1m', label: '1 minute' },
    { id: '5m', label: '5 minutes' },
    { id: '15m', label: '15 minutes' },
    { id: '30m', label: '30 minutes' },
    { id: '1h', label: '1 hour' },
    { id: '1D', label: '1 day' },
    { id: '1W', label: '1 week' },
    { id: '1M', label: '1 month' }
]

/** TechnicalsSummaryCard SVG geometry. */
const VIEW_W = 200
const VIEW_H = 104
const CX = 100
const CY = 100
const RADIUS = 78
const STROKE = 8
const NEEDLE_LEN = 56
const TRACK_PATH = `M ${CX - RADIUS} ${CY} A ${RADIUS} ${RADIUS} 0 0 1 ${CX + RADIUS} ${CY}`

const noop = () => undefined

type GaugeProps = {
    title: number
    emphasized?: boolean
}

/** Same box model as TechnicalsSummaryCard: title, spacer, gauge, rating label, counts. */
const GaugeSkeleton = ({ title, emphasized = false }: GaugeProps) => {
    const shimmerColor = useSubtleShimmerColor()

    return (
        <Box
            sx={{
                minWidth: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch'
            }}
        >
            <Box
                sx={{
                    width: title,
                    height: emphasized ? 19.5 : 16.5,
                    mx: 'auto',
                    mb: 0.25,
                    borderRadius: 0.5
                }}
            />

            {/* Absorbs the height difference vs the Summary card, exactly as the real card does. */}
            <Box data-shimmer-ignore sx={{ flex: '1 1 0', minHeight: 0, maxHeight: 40 }} />

            <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <Box sx={{ width: '100%', maxWidth: emphasized ? 240 : 180, mx: 'auto' }}>
                    {/* Painted rects can't form a ring, so the gauge is drawn here and skipped by the shimmer. */}
                    <Box
                        component="svg"
                        data-shimmer-ignore
                        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                        aria-hidden
                        sx={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
                    >
                        <path d={TRACK_PATH} fill="none" stroke={shimmerColor} strokeWidth={STROKE} strokeLinecap="butt" />
                        <line
                            x1={CX}
                            y1={CY}
                            x2={CX}
                            y2={CY - NEEDLE_LEN}
                            stroke={shimmerColor}
                            strokeWidth={2.25}
                            strokeLinecap="round"
                        />
                        <circle cx={CX} cy={CY} r={4} fill="none" stroke={shimmerColor} strokeWidth={2} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: emphasized ? 96 : 64,
                        height: emphasized ? 24 : 19.2,
                        mx: 'auto',
                        mt: 1.25,
                        mb: 1.5,
                        borderRadius: 0.5
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    {[26, 46, 24].map((captionWidth, index) => (
                        <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ width: captionWidth, height: 15.6, mx: 'auto', mb: 0.25, borderRadius: 0.5 }} />
                            <Box
                                sx={{ width: 16, height: emphasized ? 20.4 : 18.6, mx: 'auto', borderRadius: 0.5 }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

/** Divider between summary cards, drawn for real so the row width matches TechnicalsSummariesRow. */
const CardDivider = () => (
    <Box
        data-shimmer-ignore
        sx={{
            alignSelf: 'stretch',
            flexShrink: 0,
            bgcolor: 'divider',
            width: { xs: '100%', md: '1px' },
            height: { xs: '1px', md: 'auto' },
            mx: { md: 2 },
            my: { xs: 2, md: 0 }
        }}
    />
)

type TableRowProps = {
    height: number
    children: React.ReactNode
}

const TableRowSkeleton = ({ height, children }: TableRowProps) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            height,
            borderBottom: '1px solid',
            borderColor: 'divider'
        }}
    >
        {children}
    </Box>
)

type TableProps = {
    titleWidth: number
    rowCount: number
}

/** Oscillators / Moving Averages table. */
const TableSkeleton = ({ titleWidth, rowCount }: TableProps) => (
    <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ width: titleWidth, height: TABLE_TITLE_HEIGHT, mb: 1, borderRadius: 0.5 }} />
        <TableRowSkeleton height={TH_HEIGHT}>
            <Box sx={{ flex: 1, minWidth: 0, px: CELL_PADDING_X }}>
                <Box sx={{ width: 38, height: 18, borderRadius: 0.5 }} />
            </Box>
            <Box sx={{ px: CELL_PADDING_X }}>
                <Box sx={{ width: 34, height: 18, borderRadius: 0.5 }} />
            </Box>
            <Box sx={{ px: CELL_PADDING_X }}>
                <Box sx={{ width: 40, height: 18, borderRadius: 0.5 }} />
            </Box>
        </TableRowSkeleton>
        {Array.from({ length: rowCount }).map((_, index) => (
            <TableRowSkeleton key={index} height={TD_HEIGHT}>
                <Box sx={{ flex: 1, minWidth: 0, px: CELL_PADDING_X }}>
                    <Box
                        sx={{
                            width: index % 3 === 0 ? '72%' : '56%',
                            maxWidth: 220,
                            height: 20.25,
                            borderRadius: 0.5
                        }}
                    />
                </Box>
                <Box sx={{ px: CELL_PADDING_X }}>
                    <Box sx={{ width: 52, height: 20.25, borderRadius: 0.5 }} />
                </Box>
                <Box sx={{ px: CELL_PADDING_X }}>
                    <Box sx={{ width: 40, height: 20.25, borderRadius: 0.5 }} />
                </Box>
            </TableRowSkeleton>
        ))}
    </Box>
)

/**
 * Structure-only placeholder measured by SubtleShimmer, which paints one rect per leaf box.
 */
function TechnicalsSkeleton() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                bgcolor: 'background.paper'
            }}
        >
            <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{ width: LOGO_SIZE, height: LOGO_SIZE, borderRadius: '50%', flexShrink: 0 }} />
                        <Box sx={{ width: 180, height: HEADER_NAME_HEIGHT, borderRadius: 0.5 }} />
                    </Box>
                    <Box
                        sx={{
                            width: BACK_BUTTON_WIDTH,
                            height: BACK_BUTTON_HEIGHT,
                            borderRadius: 1,
                            flexShrink: 0
                        }}
                    />
                </Box>
                <TechnicalsTimeframeToggle timeframes={TIMEFRAME_PLACEHOLDERS} value="" onChange={noop} />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: 2,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'stretch',
                        width: '100%',
                        mb: 3
                    }}
                >
                    <Box sx={{ flex: 0.95, minWidth: 0, px: { md: 0.5 }, display: 'flex' }}>
                        <GaugeSkeleton title={58} />
                    </Box>
                    <CardDivider />
                    <Box sx={{ flex: 1.25, minWidth: 0, px: { md: 0.5 }, display: 'flex' }}>
                        <GaugeSkeleton title={52} emphasized />
                    </Box>
                    <CardDivider />
                    <Box sx={{ flex: 0.95, minWidth: 0, px: { md: 0.5 }, display: 'flex' }}>
                        <GaugeSkeleton title={84} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'flex-start',
                        gap: 4,
                        width: '100%',
                        mt: 1
                    }}
                >
                    <TableSkeleton titleWidth={68} rowCount={OSCILLATOR_ROWS} />
                    <TableSkeleton titleWidth={112} rowCount={MOVING_AVERAGE_ROWS} />
                </Box>

                <Box sx={{ width: '100%', mt: 3 }}>
                    <Box sx={{ width: 40, height: TABLE_TITLE_HEIGHT, mb: 1, borderRadius: 0.5 }} />
                    <TableRowSkeleton height={TH_HEIGHT}>
                        <Box sx={{ width: 68, px: CELL_PADDING_X, flexShrink: 0 }}>
                            <Box sx={{ width: 34, height: 18, borderRadius: 0.5 }} />
                        </Box>
                        {PIVOT_COLUMN_WIDTHS.map((width, index) => (
                            <Box key={index} sx={{ flex: 1, maxWidth: width, px: CELL_PADDING_X }}>
                                <Box sx={{ width: '70%', height: 18, ml: 'auto', borderRadius: 0.5 }} />
                            </Box>
                        ))}
                    </TableRowSkeleton>
                    {Array.from({ length: PIVOT_ROWS }).map((_, rowIndex) => (
                        <TableRowSkeleton key={rowIndex} height={TD_HEIGHT}>
                            <Box sx={{ width: 68, px: CELL_PADDING_X, flexShrink: 0 }}>
                                <Box sx={{ width: 24, height: 20.25, borderRadius: 0.5 }} />
                            </Box>
                            {PIVOT_COLUMN_WIDTHS.map((width, colIndex) => (
                                <Box key={colIndex} sx={{ flex: 1, maxWidth: width, px: CELL_PADDING_X }}>
                                    <Box sx={{ width: '66%', height: 20.25, ml: 'auto', borderRadius: 0.5 }} />
                                </Box>
                            ))}
                        </TableRowSkeleton>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

export default memo(TechnicalsSkeleton)
